import { AlertDialog, Button, Spinner } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

import type { ProductSummary } from '../types'
import { translated } from '../types'

interface DeleteProductDialogProps {
  product: ProductSummary | null
  onOpenChange: (isOpen: boolean) => void
  onConfirm: () => void
  isDeleting: boolean
  error?: Error | null
}

/**
 * Deleting takes the variants with it, which is not obvious from a row that shows
 * one name — so the count is spelled out before the merchant commits.
 */
export function DeleteProductDialog({
  product,
  onOpenChange,
  onConfirm,
  isDeleting,
  error,
}: DeleteProductDialogProps) {
  return (
    <AlertDialog isOpen={Boolean(product)} onOpenChange={onOpenChange}>
      <AlertDialog.Backdrop className="overlay-scope" variant="blur">
        <AlertDialog.Container size="sm" placement="center">
          <AlertDialog.Dialog role="alertdialog">
            <AlertDialog.Header className="gap-1">
              <AlertDialog.Heading>Delete this product?</AlertDialog.Heading>
            </AlertDialog.Header>

            <AlertDialog.Body className="flex flex-col gap-3">
              <p className="text-muted text-sm leading-relaxed">
                <span className="text-foreground font-medium">
                  {product ? translated(product.name) : ''}
                </span>{' '}
                will be removed
                {product && product.numberOfVariants > 1
                  ? `, along with its ${product.numberOfVariants} variants.`
                  : '.'}{' '}
                This cannot be undone.
              </p>

              {error && (
                <p className="text-danger text-xs" role="alert">
                  {errorMessage(error, 'Could not delete this product. Please try again.')}
                </p>
              )}
            </AlertDialog.Body>

            <AlertDialog.Footer>
              <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button variant="primary" onPress={onConfirm} isDisabled={isDeleting}>
                {isDeleting && <Spinner size="sm" />}
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    </AlertDialog>
  )
}
